import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

// A flake in the storm: how near it is (0 far .. 2 near) sets its size, speed and softness.
interface Flake {
  x: number;
  y: number;
  layer: number;
  size: number;
  drift: number;
  phase: number;
}

// A veil of blowing snow: a soft, wide gust of white that races across with the wind.
interface Veil {
  x: number;
  y: number;
  rx: number;
  ry: number;
  speed: number;
  alpha: number;
}

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/**
 * Everest's hero board (owner, 2026-10-16: not a plain mountain; a snowstorm, and Everest's summit
 * far off in the middle): a blizzard driven slant across the stage in three depths, the near flakes
 * big and soft, the far ones fine, veils of blowing snow racing through; and in the middle, far off,
 * the massif in its haze, Everest's summit between Nuptse and Lhotse, snow on its upper slopes, its
 * foot in a sea of cloud. The ground is an icy mesh so the white snow shows on a light theme, the
 * colour circle's sunrise coral glowing up behind the massif. A gust (the pointer, a finger, the
 * counter counting) whips the storm up, and the snow swirls round the pointer.
 */
@Component( {
  selector: 'app-hero-everest',
  template: '<canvas #canvas class="he-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.he-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroEverestComponent extends HeroMotifBase {

  private flakes: Flake[] = [];
  private veils: Veil[] = [];
  // the ridge's lower edge, sampled across the width (page pixels from the top), and its peaks
  private unitW = 0;
  private clouds: HTMLCanvasElement | null = null;
  private range: { back: { x: number; y: number }[]; main: { x: number; y: number }[]; front: { x: number; y: number }[]; peaks: { x: number; y: number; s: number }[] } | null = null;
  // the massif, painted once (it does not move) and drawn from here each frame
  private slope: HTMLCanvasElement | null = null;
  private dot: HTMLCanvasElement | null = null;
  private soft: HTMLCanvasElement | null = null;
  private scaleK = 1;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.6;
  }

  protected colorsChanged(): void {
    this.dot = null;
    this.slope = null;
  }

  protected speed(): number {
    return 0.6 + 0.9 * this.energy;
  }

  // ---- the ridge and the storm ----

  protected build( rand: () => number ): void {
    const W = this.width;
    const H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    const k = this.scaleK;
    // the massif, far off in the middle (owner, 2026-10-16: the summit far away, in the middle):
    // Everest's top over the centre, Nuptse's lower wall to the left and Lhotse to the right, a paler
    // range behind them and misty foothills in front, each ridge line roughened by halving and pushing
    // the massif a little nearer and grander than the range behind it (owner): its points spread out
    // round the summit and stretched up from its foot, the whole a little higher
    const near = ( [ u, v ]: [ number, number ] ): [ number, number ] => [ 0.52 + ( u - 0.52 ) * 1.2, 0.82 - ( 0.82 - v ) * 1.4 - 0.02 ];
    // the mountains keep their proportions on any width (owner: a wide screen stretched them): across,
    // a unit of the stage's height times its usual aspect, capped by the width
    const UW = Math.min( W, H * 2.1 );
    this.unitW = UW;
    const toX = ( u: number ) => W * 0.52 + ( u - 0.52 ) * UW;
    const ridgeOf = ( pts: [ number, number ][], rough: number, sink = true ) => {
      const out: { x: number; y: number }[] = [];
      for ( let i = 1; i < pts.length; i++ ) {
        const seg = this.jagged( { x: toX( pts[ i - 1 ][ 0 ] ), y: pts[ i - 1 ][ 1 ] * H }, { x: toX( pts[ i ][ 0 ] ), y: pts[ i ][ 1 ] * H }, 5, rough, rand );
        out.push( ...( i === 1 ? seg : seg.slice( 1 ) ) );
      }
      // past its ends (on a wide stage) the massif sinks away under the cloud, slowly, along a concave
      // curve: steep at first, easing out toward the cloud, never running on flat (owner)
      if ( sink ) {
        const fall = ( from: { x: number; y: number }, dir: number ) => {
          const pts2: { x: number; y: number }[] = [];
          for ( let j = 1; j <= 8; j++ ) {
            const t = j / 8;
            pts2.push( { x: from.x + dir * UW * 0.45 * t, y: from.y + ( H * 0.97 - from.y ) * ( 1 - ( 1 - t ) * ( 1 - t ) ) } );
          }
          return pts2;
        };
        if ( out[ 0 ].x > -10 ) { out.unshift( ...fall( out[ 0 ], -1 ).reverse() ); }
        if ( out[ out.length - 1 ].x < W + 10 ) { out.push( ...fall( out[ out.length - 1 ], 1 ) ); }
      }
      if ( out[ 0 ].x > -10 ) { out.unshift( { x: -10, y: out[ 0 ].y } ); }
      if ( out[ out.length - 1 ].x < W + 10 ) { out.push( { x: W + 10, y: out[ out.length - 1 ].y } ); }
      return out;
    };
    this.range = {
      // far away (owner: further still, like the macOS mountain wallpaper): small in the frame, rising
      // out of a sea of cloud
      // the range behind, nearer and higher too (owner)
      // and running on across a wide stage: its line repeated to either side (owner)
      back: ridgeOf( [ -1.04, 0, 1.04 ].reduce( ( all, off ) => all.concat(
        ( [ [ -0.02, 0.7 ], [ 0.14, 0.66 ], [ 0.3, 0.7 ], [ 0.42, 0.66 ], [ 0.66, 0.67 ], [ 0.82, 0.64 ], [ 1.02, 0.7 ] ] as [ number, number ][] )
          .map( ( [ u, v ] ) => [ u + off, 0.85 - ( 0.85 - v ) * 1.15 - 0.02 ] as [ number, number ] ) ), [] as [ number, number ][] ), 0.04, false ),
      // Everest as photographs show it (owner): a broad pyramid, its west ridge on the left steeper
      // with a shoulder, its north-east ridge on the right long and stepped, then the dip of the South
      // Col and Lhotse rising nearly as high
      // the summit a little higher (owner), the west ridge running down to the hill on the left in one
      // natural sweep
      main: ridgeOf( [ [ -0.02, 0.82 ], [ 0.18, 0.75 ], [ 0.31, 0.67 ], [ 0.36, 0.665 ], [ 0.41, 0.645 ], [ 0.445, 0.6 ],
        [ 0.47, 0.565 ], [ 0.492, 0.525 ], [ 0.512, 0.492 ], [ 0.522, 0.496 ], [ 0.54, 0.52 ], [ 0.552, 0.534 ], [ 0.57, 0.565 ],
        // Lhotse unlike Everest and clearly lower than it (owner): a sharp needle rising steep out of the South Col, then its long
        // serrated crest running east, pinnacle after pinnacle
        [ 0.585, 0.612 ], [ 0.603, 0.6 ], [ 0.613, 0.585 ], [ 0.62, 0.572 ], [ 0.626, 0.58 ], [ 0.636, 0.588 ],
        // (where the steep descent meets the gentler slope below, a short concave curve, owner)
        [ 0.643, 0.583 ], [ 0.651, 0.595 ], [ 0.659, 0.59 ], [ 0.67, 0.605 ], [ 0.688, 0.625 ], [ 0.706, 0.649 ],
        [ 0.717, 0.662 ], [ 0.729, 0.672 ], [ 0.744, 0.68 ], [ 0.84, 0.72 ], [ 1.02, 0.78 ] ].map( near ), 0.022 ),
      front: [],
      peaks: ( [ [ 0.512, 0.492, 1 ], [ 0.31, 0.67, 0.5 ], [ 0.62, 0.572, 0.6 ] ] as [ number, number, number ][] ).map( ( [ u, v, s ] ) => {
        const [ x, y ] = near( [ u, v ] );
        return { x: toX( x ), y: y * H, s };
      } ),
    };
    this.slope = null;
    // the storm in three depths: many fine far flakes, fewer mid, a few big near ones
    this.flakes = [];
    const area = W * H;
    const counts = [ area / 1600, area / 4200, area / 26000 ];
    counts.forEach( ( c, layer ) => {
      for ( let i = 0; i < c; i++ ) {
        this.flakes.push( {
          x: rand() * W, y: rand() * H, layer,
          size: ( layer === 0 ? 1.2 + rand() * 1.4 : layer === 1 ? 2.6 + rand() * 2.6 : 7 + rand() * 7 ) * k,
          drift: 0.75 + rand() * 0.5, phase: rand() * Math.PI * 2,
        } );
      }
    } );
    this.veils = [];
    for ( let i = 0; i < 5; i++ ) {
      this.veils.push( this.veil( rand() * W, rand ) );
    }
  }

  private veil( x: number, rand: () => number = Math.random ): Veil {
    const k = this.scaleK;
    return {
      x, y: this.height * ( 0.15 + rand() * 0.8 ), rx: ( 220 + rand() * 260 ) * k, ry: ( 40 + rand() * 50 ) * k,
      speed: 0.8 + rand() * 0.6, alpha: 0.1 + rand() * 0.12,
    };
  }

  /** The wind now: strong and slanting down to the right, gusting in its loop, wilder when awake. */
  private wind(): [ number, number ] {
    const t = this.time;
    const gust = 1 + 0.35 * Math.sin( t * W0 * 3 ) + 0.2 * Math.sin( t * W0 * 7 + 1 );
    const s = ( 230 + 330 * this.energy ) * gust * this.scaleK;
    return [ s, s * 0.32 ];
  }

  private blow( dt: number ): void {
    const W = this.width;
    const H = this.height;
    const [ wx, wy ] = this.wind();
    const t = this.time;
    for ( const f of this.flakes ) {
      const depth = f.layer === 0 ? 0.45 : f.layer === 1 ? 0.75 : 1.25;
      // eddies: the air folds as it blows, so the flakes rise and dip on their way
      const eddy = Math.sin( f.x * 0.006 + t * W0 * 9 + f.phase ) * 60 * depth;
      let vx = wx * depth * f.drift;
      let vy = ( wy + 40 ) * depth * f.drift + eddy;
      if ( this.near > 0.01 ) {
        // the pointer stirs the storm: flakes near it are thrown round it
        const dx = f.x - this.pointerX;
        const dy = f.y - this.pointerY;
        const d2 = dx * dx + dy * dy;
        const d = Math.sqrt( d2 ) || 1;
        const s = this.near * Math.exp( -d2 / ( 2 * 110 * 110 ) ) * 520 * depth;
        vx += ( -dy / d * 1.2 + dx / d * 0.4 ) * s;
        vy += ( dx / d * 1.2 + dy / d * 0.4 ) * s;
      }
      f.x += vx * dt;
      f.y += vy * dt;
      if ( f.x > W + 20 ) { f.x -= W + 40; f.y = Math.random() * H; }
      if ( f.y > H + 20 ) { f.y -= H + 40; f.x = Math.random() * W; }
      if ( f.x < -20 ) { f.x += W + 40; }
      if ( f.y < -20 ) { f.y += H + 40; }
    }
    for ( const v of this.veils ) {
      v.x += wx * v.speed * dt;
      v.y += wy * v.speed * 0.5 * dt;
      if ( v.x - v.rx > W ) { Object.assign( v, this.veil( -v.rx ) ); }
    }
  }

  // ---- the drawing ----

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    if ( !this.dot ) { this.makeSprites(); }
    if ( dt > 0 ) { this.blow( dt ); }
    const [ ice, blue, deep, coral ] = this.five();
    const W = this.width;
    const H = this.height;
    const e = this.energy;
    const white: RGB = [ 255, 255, 255 ];

    // the ground: an icy mesh, cold blue high up, pale ice below, a faint sunrise low on the right
    if ( !this.frozen ) {
      const sky = ctx.createLinearGradient( 0, 0, 0, H );
      // deep enough that the white snow shows against it
      sky.addColorStop( 0, this.rgba( this.mix( blue, white, 0.3 ), 0.85 ) );
      sky.addColorStop( 0.5, this.rgba( this.mix( blue, ice, 0.6 ), 0.7 ) );
      sky.addColorStop( 1, this.rgba( this.mix( ice, blue, 0.15 ), 0.55 ) );
      ctx.fillStyle = sky;
      ctx.fillRect( 0, 0, W, H );
      const R = Math.max( W, H );
      const patches: [ number, number, number, RGB, number ][] = [
        // the sunrise glowing up from behind the massif (owner: the light is behind the mountain)
        [ 0.58, 0.68, 0.85, this.mix( coral, white, 0.2 ), 0.72 ],
        [ 0.2, 0.75, 0.5, this.mix( deep, white, 0.7 ), 0.3 ],
        [ 0.55, 0.45, 0.45, white, 0.55 ],
      ];
      for ( const [ px, py, pr, c, a ] of patches ) {
        const g = ctx.createRadialGradient( px * W, py * H, 0, px * W, py * H, pr * R );
        g.addColorStop( 0, this.rgba( c, a ) );
        g.addColorStop( 1, this.rgba( c, 0 ) );
        ctx.fillStyle = g;
        ctx.fillRect( 0, 0, W, H );
      }
    }

    // the massif far off, behind the whole storm
    if ( !this.frozen ) { this.drawRange( ctx ); }

    // the far snow
    this.drawFlakes( ctx, 0 );

    // the veils of blowing snow
    for ( const v of this.veils ) {
      ctx.save();
      ctx.translate( v.x, v.y );
      ctx.scale( v.rx, v.ry );
      const g = ctx.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      g.addColorStop( 0, this.rgba( white, v.alpha * ( 1 + 1.2 * e ) ) );
      g.addColorStop( 1, this.rgba( white, 0 ) );
      ctx.fillStyle = g;
      ctx.fillRect( -1, -1, 2, 2 );
      ctx.restore();
    }


    // the mid and near snow in front of it
    this.drawFlakes( ctx, 1 );
    this.drawFlakes( ctx, 2 );
  }

  /** The massif, painted once (it does not move). */
  private drawRange( ctx: CanvasRenderingContext2D ): void {
    const scale = ctx.getTransform().a;
    if ( !this.slope ) { this.bakeRange( scale ); }
    if ( !this.slope || !this.range ) { return; }
    ctx.drawImage( this.slope, 0, 0, this.width, this.height );
    if ( this.clouds ) {
      // a slow drift to the right, a few pixels a second, wrapping round
      const now = performance.now() / 1000;
      const off = ( now * 16 * this.scaleK ) % this.width;
      ctx.drawImage( this.clouds, off, 0, this.width, this.height );
      ctx.drawImage( this.clouds, off - this.width, 0, this.width, this.height );
    }
  }

  /**
   * The massif's picture, far off and soft, in the manner of a mountain wallpaper: a pale range behind;
   * the massif's tops bright with snow fading down into blue rock and haze, every peak's right-hand
   * face lit a little paler in a soft wash (the sunrise is behind it, in the sky) (no hard edges inside the mountain); and a sea of cloud in long flat layers over the massif's foot.
   */
  private bakeRange( scale: number ): void {
    const r = this.range;
    if ( !r ) { return; }
    const [ ice, blue, deep, coral ] = this.five();
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    const g = c.getContext( '2d' )!;
    g.scale( scale, scale );
    const white: RGB = [ 255, 255, 255 ];
    const haze = this.mix( this.mix( blue, white, 0.55 ), ice, 0.3 );
    const rand = this.seededLocal( 29 );
    const outline = ( pts: { x: number; y: number }[] ) => {
      g.beginPath();
      g.moveTo( -10, H + 10 );
      pts.forEach( p => g.lineTo( p.x, p.y ) );
      g.lineTo( W + 10, H + 10 );
      g.closePath();
    };
    const summit = r.peaks[ 0 ];
    // the far range
    outline( r.back );
    const bg = g.createLinearGradient( 0, H * 0.56, 0, H * 0.8 );
    bg.addColorStop( 0, this.rgba( this.mix( haze, blue, 0.25 ), 1 ) );
    bg.addColorStop( 1, this.rgba( this.mix( haze, white, 0.3 ), 1 ) );
    g.fillStyle = bg;
    g.fill();
    // the massif: snow bright at the tops, blue rock below, sinking into the haze
    // the rock toned down into the haze (owner: softer below the snow)
    const rock = this.mix( this.mix( deep, [ 34, 50, 66 ], 0.35 ), haze, 0.6 );
    const snowTop = this.mix( ice, white, 0.7 );
    outline( r.main );
    // the rock, sinking into the haze toward its foot
    const mg = g.createLinearGradient( 0, summit.y, 0, H * 0.8 );
    mg.addColorStop( 0, this.rgba( this.mix( rock, snowTop, 0.25 ), 1 ) );
    mg.addColorStop( 0.6, this.rgba( rock, 1 ) );
    mg.addColorStop( 1, this.rgba( this.mix( rock, haze, 0.7 ), 1 ) );
    g.fillStyle = mg;
    g.fill();
    // the snow over the tops, down to a snowline that winds in an S (owner: not a level gradient),
    // its edge soft
    g.save();
    outline( r.main );
    g.clip();
    // the snowline winds in an S across the massif (owner: not a level gradient)
    // one chosen S, the same on every load (owner): the first S, the one that worked
    const ph1 = 0;
    const ph2 = 1;
    const UW = this.unitW || W;
    const l1 = UW * 0.055;
    const l2 = UW * 0.021;
    const line = ( x: number ) => {
      // the left side a little lower still, its slope drops away faster than the right's (owner)
      const left = Math.min( 1, Math.max( 0, ( summit.x - x ) / ( UW * 0.1 ) ) );
      // one wave running right through the middle (owner: neither a dip nor a bulge there), so it
      // follows x itself, not the distance from the summit
      const u = x - summit.x;
      // (the summit was raised after the snowline was set: the line keeps its height on the mountain)
      return summit.y + H * 0.305 + H * 0.035 * Math.sin( u / l1 + ph1 ) + H * 0.015 * Math.sin( u / l2 + ph2 )
        + H * 0.03 * left
        // past Lhotse the line lifts away softly, so the right side ends instead of trailing a thin tail (owner)
        - Math.pow( Math.max( 0, u - UW * 0.21 ), 2 ) / ( UW * 0.22 );
    };
    g.beginPath();
    g.moveTo( -10, -10 );
    g.lineTo( W + 10, -10 );
    for ( let x = W + 10; x >= -10; x -= 6 ) { g.lineTo( x, line( x ) ); }
    g.closePath();
    const sn = g.createLinearGradient( 0, summit.y, 0, summit.y + H * 0.2 );
    sn.addColorStop( 0, this.rgba( snowTop, 1 ) );
    sn.addColorStop( 1, this.rgba( this.mix( snowTop, rock, 0.18 ), 1 ) );
    g.shadowColor = this.rgba( this.mix( snowTop, rock, 0.2 ), 0.9 );
    g.shadowBlur = 16 * k * scale;
    g.fillStyle = sn;
    g.fill();
    g.restore();
    g.save();
    outline( r.main );
    g.clip();
    // each peak's shadowed left face and sunlit right face, as soft washes
    for ( const pk of r.peaks ) {
      const wdt = UW * 0.2 * pk.s;
      const hgt = H * 0.28 * pk.s;
      for ( const [ side, colour, a ] of [ [ -1, this.mix( blue, deep, 0.4 ), 0.22 ], [ 1, this.mix( blue, white, 0.6 ), 0.12 ] ] as [ number, RGB, number ][] ) {
        g.save();
        // centred well down the face, so no bright spot sits on the top (owner: it read as a lamp)
        g.translate( pk.x + side * wdt * 0.45, pk.y + hgt * 0.6 );
        g.scale( wdt * 0.6, hgt * 0.6 );
        const wg = g.createRadialGradient( 0, -0.3, 0, 0, 0, 1 );
        wg.addColorStop( 0, this.rgba( colour, a ) );
        wg.addColorStop( 1, this.rgba( colour, 0 ) );
        g.fillStyle = wg;
        g.fillRect( -1, -1, 2, 2 );
        g.restore();
      }
    }
    g.restore();
    // the sea of cloud: long flat layers across the foot of everything, soft on top, thickening below
    // a little lower, so more of the massif shows above it (owner)
    const base = H * 0.84;
    const cloud = g.createLinearGradient( 0, base - 20 * k, 0, H );
    cloud.addColorStop( 0, this.rgba( white, 0 ) );
    cloud.addColorStop( 0.25, this.rgba( this.mix( white, haze, 0.15 ), 0.85 ) );
    cloud.addColorStop( 1, this.rgba( white, 1 ) );
    g.fillStyle = cloud;
    g.fillRect( 0, base - 20 * k, W, H - base + 20 * k );
    // a clearing of haze behind the hero's words, so the counter and the motto always read
    for ( const b of this.blocks ) {
      g.save();
      g.translate( b.x + b.w / 2, b.y + b.h / 2 );
      g.scale( b.w / 2 + 70 * k, b.h / 2 + 40 * k );
      const cl = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      // light enough that the summit still shows through it (it read as lower than Lhotse when hidden)
      cl.addColorStop( 0, this.rgba( this.mix( haze, white, 0.6 ), 0.35 ) );
      cl.addColorStop( 1, this.rgba( this.mix( haze, white, 0.6 ), 0 ) );
      g.fillStyle = cl;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    this.slope = c;
    // the cloud's billows on a layer of their own, drifting slowly right (owner) and wrapping round, so
    // each is drawn again a stage-width to either side
    const cl = document.createElement( 'canvas' );
    cl.width = Math.ceil( W * scale );
    cl.height = Math.ceil( H * scale );
    const cg2 = cl.getContext( '2d' )!;
    cg2.scale( scale, scale );
    for ( let i = 0; i < 60; i++ ) {
      const x = rand() * W;
      const y = base - 10 * k + rand() * H * 0.2;
      const rx = ( 160 + rand() * 260 ) * k;
      const ry = ( 14 + rand() * 22 ) * k;
      for ( const dx of [ -W, 0, W ] ) {
        cg2.save();
        cg2.translate( x + dx, y );
        cg2.scale( rx, ry );
        const bg2 = cg2.createRadialGradient( 0, -0.25, 0, 0, 0, 1 );
        bg2.addColorStop( 0, this.rgba( white, 0.75 ) );
        bg2.addColorStop( 0.7, this.rgba( this.mix( white, haze, 0.25 ), 0.35 ) );
        bg2.addColorStop( 1, this.rgba( white, 0 ) );
        cg2.fillStyle = bg2;
        cg2.fillRect( -1, -1, 2, 2 );
        cg2.restore();
      }
    }
    this.clouds = cl;
  }

  /** A line from a to b, its middle pushed sideways again and again: a ridge's broken line. */
  private jagged( a: { x: number; y: number }, b: { x: number; y: number }, levels: number, rough: number, rand: () => number ): { x: number; y: number }[] {
    let pts = [ a, b ];
    let push = Math.hypot( b.x - a.x, b.y - a.y ) * rough;
    for ( let l = 0; l < levels; l++ ) {
      const next = [ pts[ 0 ] ];
      for ( let i = 1; i < pts.length; i++ ) {
        const p = pts[ i - 1 ], q = pts[ i ];
        const dx = q.x - p.x, dy = q.y - p.y;
        const len = Math.hypot( dx, dy ) || 1;
        const off = ( rand() - 0.5 ) * 2 * push;
        next.push( { x: ( p.x + q.x ) / 2 - dy / len * off, y: ( p.y + q.y ) / 2 + dx / len * off }, q );
      }
      pts = next;
      push *= 0.55;
    }
    return pts;
  }

  /** A small seeded random of its own, so the slope's lumps are the same each time it is painted. */
  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** One depth of the storm: far flakes as fine dots, mid and near ones as soft streaks along the wind. */
  private drawFlakes( ctx: CanvasRenderingContext2D, layer: number ): void {
    const [ wx, wy ] = this.wind();
    const ang = Math.atan2( wy + 40, wx );
    const stretch = 1 + Math.min( 2.4, Math.hypot( wx, wy ) / ( 260 * this.scaleK ) ) * ( layer === 0 ? 0.4 : 1 );
    const img = layer === 2 ? this.soft! : this.dot!;
    const alpha = layer === 0 ? 0.8 : layer === 1 ? 0.95 : 0.8;
    ctx.globalAlpha = alpha;
    const c = Math.cos( ang );
    const s = Math.sin( ang );
    const scale = ctx.getTransform().a;
    for ( const f of this.flakes ) {
      if ( f.layer !== layer ) { continue; }
      const w = f.size * stretch;
      const h = f.size;
      ctx.setTransform( c * scale, s * scale, -s * scale, c * scale, f.x * scale, f.y * scale );
      ctx.drawImage( img, -w / 2, -h / 2, w, h );
    }
    ctx.setTransform( scale, 0, 0, scale, 0, 0 );
    ctx.globalAlpha = 1;
  }

  /**
   * A flake's dot, white with a faint cold rim so it reads on the pale ice, and a softer, blurred one
   * for the near flakes.
   */
  private makeSprites(): void {
    const [ , blue ] = this.five();
    const make = ( size: number, hard: number ) => {
      const c = document.createElement( 'canvas' );
      c.width = c.height = size;
      const g = c.getContext( '2d' )!;
      const r = size / 2;
      const grad = g.createRadialGradient( r, r, 0, r, r, r );
      grad.addColorStop( 0, 'rgba(255,255,255,1)' );
      grad.addColorStop( hard, 'rgba(255,255,255,0.95)' );
      grad.addColorStop( Math.min( 0.98, hard + 0.12 ), this.rgba( this.mix( blue, [ 255, 255, 255 ], 0.55 ), 0.35 ) );
      grad.addColorStop( 1, 'rgba(255,255,255,0)' );
      g.fillStyle = grad;
      g.fillRect( 0, 0, size, size );
      return c;
    };
    this.dot = make( 16, 0.55 );
    this.soft = make( 48, 0.2 );
  }

  /** The colour circle's five, or Everest's own: ice, blue, deep teal, sunrise coral, maroon. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 228, 236, 239 ], [ 26, 109, 159 ], [ 0, 94, 121 ], [ 237, 126, 90 ], [ 143, 68, 70 ] ];
  }
}
